import type { CSSProperties, JSX } from "react";

import { CITY_PAGE_PREVIEW_COUNT, SECTION_TYPES } from "@noko-city/shared";

import { AtlasCta } from "../../AtlasCta";
import { HeadingGlyph } from "../../GuideSection";
import { InstagramPostCard } from "../../InstagramPostCard";
import { Reveal } from "../../Reveal";
import type { SectionRenderInput } from "./types";

/**
 * 2. LATEST ON INSTAGRAM — first page readable, everything else on IG.
 *
 * The visible label is decorative and a screen-reader-only heading names the
 * section, so heading navigation reaches the page's first content block without
 * the label being printed twice: the kicker prints the section's own heading, and
 * the heading is the name the registry gives the section. The row draws
 * `section.limit` covers, which the payload cannot exceed anyway.
 *
 * A city with no handle, or with no published cover, has no post row to draw and
 * renders nothing at all — the payload's own `rendered` flag is what decides
 * whether the section is in the document.
 */
export function InstagramSection({ section, page }: SectionRenderInput): JSX.Element | null {
  const handle = page.instagramHandle;
  const posts = (page.instagramPosts ?? []).slice(0, section.limit ?? CITY_PAGE_PREVIEW_COUNT);
  if (!handle || posts.length === 0) return null;
  return (
    <Reveal variant="stamp" delay={160} className="atlas-guide-hero-posts">
      <section className="atlas-guide-col atlas-guide-posts">
        <h2 className="sr-only">{SECTION_TYPES.instagram.label}</h2>
        <p className="atlas-kicker" aria-hidden="true">
          {/* The registry gives this block a glyph like any other headed section,
              so it is printed here too — a setting the studio offers and the page
              ignores is worse than no setting. */}
          <HeadingGlyph icon={section.icon} />
          {section.heading}
        </p>
        {/* One column per post: the payload carries at most
          CITY_PAGE_PREVIEW_COUNT (5) covers, so this is the hero's
          single row and its frames are sized to the space the row has. */}
        <ul className="atlas-guide-grid" style={{ "--cover-cols": posts.length } as CSSProperties}>
          {posts.map((post, position) => (
            <li key={post.id}>
              <Reveal variant="stamp" delay={position * 60}>
                <InstagramPostCard post={post} cityName={page.name} priority={position === 0} />
              </Reveal>
            </li>
          ))}
        </ul>
        <AtlasCta
          href={`https://instagram.com/${handle}`}
          label={`FOLLOW @${handle.toUpperCase()}`}
        />
      </section>
    </Reveal>
  );
}
